import { apiService } from "./api";

export interface SiteSetting {
    setting_id: string;
    key: string;
    value: string | null;
    value_type: "text" | "image" | "json" | "html" | "number" | "boolean";
    group: string;
    label: string;
    description: string | null;
    display_order: number;
    create_time: string;
    update_time: string;
}

export interface SiteSettingUpdate {
    value?: string | null;
    label?: string;
    description?: string;
    display_order?: number;
}

export interface SiteSettingCreate {
    key: string;
    value?: string | null;
    value_type: string;
    group: string;
    label: string;
    description?: string;
    display_order?: number;
}

export type GroupedSettings = Record<string, SiteSetting[]>;

const GROUP_LABELS: Record<string, string> = {
    hero: "Hero Section",
    contact: "Contact Information",
    about: "About Page",
    why_choose_us: "Why Choose Us",
    footer: "Footer",
    general: "General",
    newsletter: "Newsletter",
    accounting: "Accounting",
};

const GROUP_ORDER = [
    "general",
    "hero",
    "about",
    "why_choose_us",
    "contact",
    "footer",
    "newsletter",
];

class SiteSettingsService {
    async getAll(group?: string): Promise<SiteSetting[]> {
        const params = group ? `?group=${group}` : "";
        return apiService.get<SiteSetting[]>(`/site-settings${params}`);
    }

    async getGrouped(): Promise<GroupedSettings> {
        const all = await this.getAll();
        const grouped: GroupedSettings = {};
        for (const setting of all) {
            if (!grouped[setting.group]) {
                grouped[setting.group] = [];
            }
            grouped[setting.group].push(setting);
        }
        return grouped;
    }

    async getByKey(key: string): Promise<SiteSetting> {
        return apiService.get<SiteSetting>(`/site-settings/${key}`);
    }

    async updateByKey(
        key: string,
        data: SiteSettingUpdate,
    ): Promise<SiteSetting> {
        return apiService.put<SiteSetting>(`/site-settings/${key}`, data);
    }

    async bulkUpdate(settings: Record<string, string>): Promise<SiteSetting[]> {
        return apiService.put<SiteSetting[]>(`/site-settings`, { settings });
    }

    async create(data: SiteSettingCreate): Promise<SiteSetting> {
        return apiService.post<SiteSetting>(`/site-settings`, data);
    }

    async deleteByKey(key: string): Promise<void> {
        return apiService.delete(`/site-settings/${key}`);
    }

    async uploadImage(key: string, file: File): Promise<SiteSetting> {
        const formData = new FormData();
        formData.append("file", file);
        return apiService.post<SiteSetting>(
            `/site-settings/${key}/upload-image`,
            formData,
            {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 30000,
            },
        );
    }

    async seedDefaults(): Promise<{ created: number; message: string }> {
        return apiService.post<{ created: number; message: string }>(
            `/site-settings/seed`,
            {},
        );
    }

    getGroupLabel(group: string): string {
        return (
            GROUP_LABELS[group] ||
            group.charAt(0).toUpperCase() + group.slice(1)
        );
    }

    getGroupOrder(): string[] {
        return GROUP_ORDER;
    }
}

const siteSettingsService = new SiteSettingsService();
export default siteSettingsService;
